import { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "../components/ui/alert-dialog";

import { Plus, Search, Trash2, Eye, Percent } from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import { server_post_data } from "../ServiceConnection/serviceconnection";
import { cn } from "../lib/utils";

import {
  save_update_combo_rule,
  view_combo_rules,
  get_view_category,
} from "../ServiceConnection/serviceconnection";

// ✅ DEFAULT FORM
const defaultForm = {
  name: "",
  conditions: [{ type: "category", value: "", min_qty: 1 }],
  action: {
    discount_type: "PERCENTAGE",
    value: "",
    apply_on: "COMBO_ITEMS",
  },
  behavior: {
    min_order_value: "",
  },
};

export default function ComboRules() {
  const { toast } = useToast();

  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const [selectedRule, setSelectedRule] = useState(null);
  const [viewRule, setViewRule] = useState(null);

  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const [formData, setFormData] = useState(defaultForm);

  // 🔄 Fetch Rules
  const fetchRules = useCallback(async () => {
    setLoading(true);

    const fd = new FormData();
    const res = await server_post_data(view_combo_rules, fd);
    setRules(res.data?.message || []);

    setLoading(false);
  }, []);

  // 🔄 Fetch Categories
  const fetchCategories = async () => {
    const fd = new FormData();
    fd.append("flag", 1);

    const res = await server_post_data(get_view_category, fd);

    if (!res.data.error) {
      setCategories(res.data.message || []);
    }
  };

  useEffect(() => {
    fetchRules();
    fetchCategories();
  }, []);

  const filteredRules = rules?.filter((r) =>
    r.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // 👁 View
  const handleView = (rule) => {
    setViewRule(rule);
    setIsViewOpen(true);
  };

  // ❌ Delete
  const handleDelete = (rule) => {
    setSelectedRule(rule);
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    const fd = new FormData();
    fd.append("main_id", selectedRule.primary_id);
    fd.append("flag", "d");

    await server_post_data(save_update_combo_rule, fd);
    fetchRules();
    setIsDeleteOpen(false);
  };

  // ✅ SAVE RULE
  const handleSaveRule = async () => {
    if (!formData.name || !formData.action.value) {
      toast({ title: "Fill required fields", variant: "destructive" });
      return;
    }

    const fd = new FormData();
    fd.append("name", formData.name);

    fd.append(
      "conditions",
      JSON.stringify({
        type: "CATEGORY_COMBINATION",
        operator: "ALL",
        rules: formData.conditions,
      }),
    );

    fd.append("action", JSON.stringify(formData.action));
    fd.append("behavior", JSON.stringify(formData.behavior));

    const res = await server_post_data(save_update_combo_rule, fd);

    if (!res.data.error) {
      toast({ title: "Rule Created" });
      setIsAddDialogOpen(false);
      setFormData(defaultForm);
      fetchRules();
    }
  };

  return (
    <MainLayout>
      <Header title="Combo Rule Management" subtitle="Manage discount combos" />

      <div className="p-6 space-y-6">
        {/* Search + Add */}
        <div className="flex justify-between">
          <div className="relative w-[300px]">
            <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search rules..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <Button onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" /> Add Rule
          </Button>
        </div>

        {/* Table */}
        <div className="rounded-xl border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Discount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-10">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : (
                filteredRules.map((rule) => {
                  const action = rule.action || {};

                  return (
                    <TableRow key={rule.primary_id}>
                      <TableCell>{rule.name}</TableCell>

                      <TableCell>
                        {action.discount_type === "PERCENTAGE"
                          ? `${action.value || 0}%`
                          : `₹${action.value || 0}`}
                      </TableCell>

                      <TableCell>
                        <Badge
                          className={cn(
                            rule.is_active
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-200",
                          )}
                        >
                          {rule.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>

                      <TableCell className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleView(rule)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(rule)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* MODAL */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Combo Rule</DialogTitle>
            </DialogHeader>

            <div className="space-y-6">
              {/* NAME */}
              <Input
                placeholder="Rule Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />

              {/* CONDITIONS */}
              <div className="border p-4 rounded">
                <p className="font-semibold mb-2">Conditions</p>

                {formData.conditions.map((cond, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    {/* CATEGORY DROPDOWN */}
                    <select
                      className="border p-2 rounded"
                      value={cond.value}
                      onChange={(e) => {
                        const updated = [...formData.conditions];
                        updated[index].value = e.target.value;
                        setFormData({ ...formData, conditions: updated });
                      }}
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat.primary_id} value={cat.primary_id}>
                          {cat.item_category}
                        </option>
                      ))}
                    </select>

                    <Input
                      placeholder="Qty"
                      value={cond.min_qty}
                      onChange={(e) => {
                        const updated = [...formData.conditions];
                        updated[index].min_qty = e.target.value;
                        setFormData({ ...formData, conditions: updated });
                      }}
                    />
                  </div>
                ))}

                <Button
                  size="sm"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      conditions: [
                        ...formData.conditions,
                        { type: "category", value: "", min_qty: 1 },
                      ],
                    })
                  }
                >
                  + Add Condition
                </Button>
              </div>

              {/* ACTION */}
              <div className="border p-4 rounded">
                <p className="font-semibold mb-2">Action</p>

                <select
                  className="border p-2 rounded w-full mb-2"
                  value={formData.action.discount_type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      action: {
                        ...formData.action,
                        discount_type: e.target.value,
                      },
                    })
                  }
                >
                  <option value="PERCENTAGE">Percentage</option>
                  <option value="FIXED">Flat ₹</option>
                </select>

                <Input
                  placeholder={
                    formData.action.discount_type === "PERCENTAGE"
                      ? "Enter %"
                      : "Enter ₹"
                  }
                  value={formData.action.value}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      action: { ...formData.action, value: e.target.value },
                    })
                  }
                />
              </div>

              {/* MIN ORDER */}
              <div>
                <label className="text-sm">Minimum Order Value</label>
                <Input
                  placeholder="Optional"
                  value={formData.behavior.min_order_value}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      behavior: {
                        ...formData.behavior,
                        min_order_value: e.target.value,
                      },
                    })
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                onClick={() => setIsAddDialogOpen(false)}
                variant="outline"
              >
                Cancel
              </Button>
              <Button onClick={handleSaveRule}>Save Rule</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
